// THE ONE PLACE A LEG DECIDES WHAT LINE TO DRAW.
//
// Two maps draw the same thing - the scheduler's (SchedulerMap) and the route
// view's (RouteMap) - and until this file they each had their own copy of the
// decision, in slightly different words. They now share it, because a leg that
// draws as a road on one page and a straight line on the other is a bug nobody
// would think to look for.
//
// THE TWO SHAPES. The journey store holds most geometry as [[lat, lng], ...],
// but rows seeded from the old `travels` table hold [{lat:, lng:}, ...] - see
// Routing::JourneySeedTasks#travel_row, which copies the legacy column across
// verbatim. Reading only the first shape turns every one of those legs into a
// line of `undefined`, which Google draws as nothing at all. SuperMap::LatLng
// tolerates both on the Ruby side; this is its counterpart here.

// One stored coordinate, whichever way round it was written, as a Google
// {lat, lng} literal. Null for anything unreadable, so a single bad point drops
// out of the line instead of breaking it.
export const toLatLng = (point) => {
  if (Array.isArray(point)) {
    const [lat, lng] = point;
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }

  if (point && typeof point === 'object') {
    const lat = point.lat ?? point.latitude;
    const lng = point.lng ?? point.longitude;
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }

  return null;
};

// A stored geometry as a drawable path. Anything under two points is not a
// line, so it answers [] and the caller falls back.
export const toPath = (geometry) => {
  if (!Array.isArray(geometry)) return [];

  const path = geometry.map(toLatLng).filter(Boolean);
  return path.length > 1 ? path : [];
};

// WHAT TO DRAW BETWEEN TWO STOPS, and the only function either map should ask.
//
// `crowFlies` forces the straight line even where a road is known - that is the
// toggle, and it has to win, because its whole purpose is to show the shape of
// the day rather than the shape of the roads.
//
// Without it, a leg draws its road where it has one and falls back to the
// straight line where it does not. The fallback is not a failure state: a day
// planned in the background has no road geometry until somebody fetches it, and
// a straight line is an honest "we have not looked yet" rather than a gap.
//
// `isRoad` is returned rather than left to be inferred, because the two want
// drawing differently - see ROAD_STROKE and CROW_STROKE.
export const legPath = ({ geometry, from, to, crowFlies = false }) => {
  const road = crowFlies ? [] : toPath(geometry);
  if (road.length > 1) return { path: road, isRoad: true };

  const ends = [toLatLng(from), toLatLng(to)].filter(Boolean);
  return { path: ends.length === 2 ? ends : [], isRoad: false };
};

// A leg the map is still waiting on is not the same as one we know is straight.
// See RouteMap's PENDING_STROKE, which dashes it.
export const isRoadKnown = (geometry) => toPath(geometry).length > 1;
